import _ from "lodash";
import api from "@/api/cloud";
import { get, set } from "@/utils/vuex";
import fixSteps from "@/../public/nocFixSteps.json";
import config from "@/../public/config.json";
import Strings from "../../../utils/strings";

const strings = Strings.k4noc.alarms.wizard;
const RETRY_TIMEOUT = 5000;
const ROOT = { root: true };

const wait = async timeout => {
	return new Promise((resolve, reject) => {
		setTimeout(resolve, timeout);
	});
};

const replaceMultiple = (str, pattern, values) => {
	values.flat().forEach(value => (str = str.replace(pattern, value)));
	return str;
};

const state = () => ({
	alarm: null,
	loading: {
		wizard: false,
		notes: false,
	},
	expired: false,
	timeout: null,
	replacer: [],
	steps: [],
	notes: [],
	step: null,
});

const getters = {
	steps: get("steps"),

	loading: state => state.loading.wizard,
	loadingNotes: state => state.loading.notes,
	success: state => state.step && state.step.success,
	failure: state => state.step && state.step.failure,

	notes: state => _.sortBy(state.notes, ["created", "alarm_timestamp"]),
	stepText: state =>
		state.step && replaceMultiple(state.step.text, "$value$", state.replacer),

	stepNumber: state => _.findIndex(state.steps, state.step) + 1,

	wizard: state => state.alarm && _.get(fixSteps, state.alarm.alarm_type),
	hasFixSteps: () => alarmType => !!_.get(fixSteps, alarmType),
};

const mutations = {
	setTimeout: set("timeout"),
	setExpired: set("expired"),
	setReplacer: set("replacer"),
	setNotes: set("notes"),
	setStep: set("step"),

	setAlarm(state, alarm) {
		state.alarm = alarm;
	},
	setSteps(state, steps) {
		state.steps = _.cloneDeep(steps).map(step => ({
			...step,
			success: false,
			failure: false,
		}));
	},
	setLoading(state, value) {
		state.loading.wizard = value;
	},
	setLoadingNotes(state, value) {
		state.loading.notes = value;
	},
	setSuccess(state, value) {
		state.step.success = value;
	},
	setFailure(state, value) {
		state.step.failure = value;
	},

	addNextStep(state, step) {
		const i = _.findIndex(state.steps, state.step);
		state.steps.splice(i + 1, 0, step);
	},

	skipToLastStep(state) {
		const i = _.findIndex(state.steps, state.step);
		state.steps.splice(i + 1, state.steps.length - i - 2);
	},

	reset: s => Object.assign(s, state()),
};

const actions = {
	async loadWizard({ commit, getters, dispatch }, alarm) {
		commit("setLoading", true);
		commit("setAlarm", alarm);
		try {
			let steps = _.get(getters.wizard, "steps", []);
			if (getters.wizard && getters.wizard.action) {
				steps = await dispatch(getters.wizard.action, alarm.cls);
			}
			commit("setSteps", steps);
		} finally {
			commit("setLoading", false);
		}
	},

	async loadingTimeout({ state, commit }) {
		if (!state.step.waitTime) return;
		commit("setExpired", false);
		commit("setLoading", true);

		const timeout = setTimeout(() => {
			commit("setExpired", true);
		}, state.step.waitTime * 1000);

		commit("setTimeout", timeout);
	},

	async getDevice({ state }) {
		return api.getDevice(state.alarm.sys_id, state.alarm.path);
	},

	async getNotes({ state, commit }, page = 0) {
		if (!state.alarm || state.loading.notes) return state.notes;
		commit("setLoadingNotes", true);

		const actions = state.steps.map(s => s.actionName).filter(_.identity);

		const query = {
			actionType: ["Comments", ...actions],
			alarmType: state.alarm.alarm_type,
			path: state.alarm.path,
			page,
		};

		let notes = [];
		try {
			notes = (await api.getAlertHistory(state.alarm.sys_id, query)).history;
			commit("setNotes", page ? [...state.notes, ...notes] : notes);
		} finally {
			commit("setLoadingNotes", false);
		}

		return notes;
	},

	async logAction({ state, dispatch, rootGetters }, comments) {
		if (!state.alarm || (!state.step.actionName && !comments)) return;
		const payload = {
			id: state.alarm.id,
			actionType: comments ? "Comments" : state.step.actionName,
			updatedBy: rootGetters["auth/fullName"],
			comments,
		};

		const res = await dispatch(
			"tickets/deviceStatus/changeStatus",
			payload,
			ROOT
		);
		return res;
	},

	async retry({ state }, fn) {
		while (!(await fn()) && !state.expired) {
			await wait(RETRY_TIMEOUT);
		}
	},

	async connectToController({ state, dispatch, rootGetters }) {
		if (rootGetters["controller/state"] < 1) {
			await dispatch("controller/loadRemote", state.alarm.sys_id, {
				root: true,
			});
		}
		if (rootGetters["controller/connecting"])
			await dispatch("controller/waitForOnline", null, ROOT);
	},

	async testDeviceVariable(
		{ state, commit, dispatch, getters, rootGetters },
		testNode
	) {
		dispatch("loadingTimeout");
		dispatch(
			"logAction",
			`${_.startCase(state.alarm.alarm_type)} - Fix Step ${getters.stepNumber}`
		);

		await dispatch("retry", async () => {
			await dispatch("connectToController");

			const isConnected = rootGetters["controller/status"] === "CONNECTED";

			let [result, failMsg] = [isConnected, strings.fails.controllerConnection];

			try {
				if (testNode && isConnected) {
					const node = rootGetters["controller/nodes/nodeByPath"](
						state.alarm.path
					);
					[result, failMsg] = await testNode(node);
				}
			} catch (e) {
				console.error(e);
				[result, failMsg] = [false, strings.fails.generic];
			} finally {
				if (result) {
					commit("setSuccess", true);
					commit("setFailure", false);
					clearTimeout(state.timeout);
				} else {
					commit("setFailure", failMsg);
				}
			}

			return result;
		});

		commit("setLoading", false);
	},

	close({ commit, dispatch, rootGetters }) {
		if (rootGetters["controller/state"] > 0)
			dispatch("controller/reset", null, ROOT);
		commit("setNotes", []);
	},

	async testControllerConnection({ dispatch }) {
		await dispatch("testDeviceVariable");
	},

	async testControllerTemperature({ dispatch }) {
		await dispatch("testDeviceVariable", controller => {
			const temperature = _.get(controller, "variables.cpuTemperature");

			return [temperature < 65, strings.fails.cpuTemperature(temperature)];
		});
	},

	async testControllerWifi({ state, commit, dispatch }) {
		await dispatch("testDeviceVariable", controller => {
			const wifi = _.get(controller, "variables.wifi");
			if (!wifi) {
				// unreal situation caused by mock alarms
				commit("setReplacer", ["--"]);
				commit("setExpired", true);
				clearTimeout(state.timeout);
				return [false, strings.fails.noWifi];
			}
			const { rssi, noise } = wifi;
			const snr = parseInt(rssi) - parseInt(noise);
			commit("setReplacer", [snr]);

			return [snr > 25, strings.fails.weakWifi];
		});
	},

	async testDeviceBattery({ dispatch }) {
		await dispatch("testDeviceVariable", device => {
			const batteryLevel = _.get(device, "variables.batteryLevel");

			return [batteryLevel > 90, strings.fails.batteryLevel(batteryLevel)];
		});
	},

	async detectDeviceType({ getters, dispatch, rootGetters }, cls) {
		let device;
		if (cls) {
			if (!rootGetters["descriptions/groups"].length) {
				await dispatch("descriptions/load", null, ROOT);
			}
			device = _.get(rootGetters["descriptions/findByClass"](cls), "data");
		}
		if (!device) {
			try {
				device = await dispatch("getDevice");
			} catch (e) {
				if (e.response.status !== 404) throw e;
				return [{ text: "Device not found." }];
			}
		}

		const hasBatteries = _.get(device, "meta.variables.batteryLevel");
		const isThermostat = _.get(device, "meta.group") === "thermostats";
		const isException = config.notBatteryClasses.includes(
			_.get(device, "meta.cls")
		);

		const nextSteps =
			hasBatteries && !isThermostat && !isException
				? "withBattery"
				: "withoutBattery";
		return getters.wizard[nextSteps];
	},

	async testDeviceResponse({ state, commit, dispatch }) {
		await dispatch("testDeviceVariable", async device => {
			try {
				await dispatch(
					"controller/nodes/executeCommand",
					{
						node: device,
						subpath: "commands/getManufacturerSpecific",
					},
					ROOT
				);
			} catch (e) {
				if (!e.message.includes("ENETUNREACH")) throw e;
				if (state.step.text === strings.zwaveTest) {
					commit("skipToLastStep");
				} else {
					commit("addNextStep", {
						...state.step,
						text: strings.zwaveTest,
					});
				}
				return [false, strings.fails.zwave];
			}
			const nodeState = _.get(device, "variables.nodeState");

			return [nodeState === "alive", strings.fails.deadDevice];
		});
	},

	async testDeviceHealthResponse({ dispatch, rootGetters }) {
		await dispatch("testDeviceVariable", async device => {
			await dispatch(
				"controller/nodes/executeCommand",
				{
					node: rootGetters["controller/nodes/controller"],
					subpath: "Bridges/Zwave/commands/healNetworkNode",
					commandParams: {
						nodeId: _.get(device, "properties.networkId"),
					},
				},
				ROOT
			);
			const nodeState = _.get(device, "variables.nodeState");

			return [nodeState === "alive", strings.fails.deadDevice];
		});
	},

	async testDeviceHealth({ commit, dispatch }) {
		await dispatch("testDeviceVariable", device => {
			const nodeState = _.get(device, "variables.nodeState");
			return [nodeState === "alive", strings.fails.deadDevice];
		});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
